/** Map numbered vLLM prompt lines to short labels for the search detail panel. */

const QUESTION_LINE = /^\s*\d+[.)]\s*(.+)$/;
const NUMBERED_ANSWER = /^\s*(\d+)[.)]\s*(.+)$/;

const LABEL_RULES: { pattern: RegExp; label: string }[] = [
  { pattern: /\b(person|male|female|none|gender)\b/i, label: "Sex" },
  { pattern: /age\s*group/i, label: "Age" },
  { pattern: /skin\s*color/i, label: "Skin" },
  { pattern: /hair\s*color/i, label: "Hair" },
  { pattern: /headwear/i, label: "Headwear" },
  { pattern: /upper\s*body/i, label: "Upper clothing" },
  { pattern: /lower\s*body/i, label: "Lower clothing" },
  { pattern: /footwear/i, label: "Footwear" },
  { pattern: /accessor/i, label: "Accessories" },
  { pattern: /activity/i, label: "Activity" },
];

const NEG = new Set(["no", "none", "n", "false", "n/a", "na", "unknown", "—", "-"]);
const AFFIRM = new Set(["yes", "yeah", "y", "true"]);

export type DescriptionRow = { label: string; answer: string };

function stripYesPrefix(text: string): string {
  const t = text.trim();
  const lower = t.toLowerCase();
  if (AFFIRM.has(lower)) return "";
  if (lower.startsWith("yes, ")) return t.slice(5).trim();
  if (lower.startsWith("yes ")) return t.slice(4).trim();
  return t;
}

function isNegative(answer: string): boolean {
  const text = answer.trim().toLowerCase().replace(/\.$/, "");
  if (!text || NEG.has(text)) return true;
  if (AFFIRM.has(text)) return true;
  if (text.startsWith("no ") || text.startsWith("no,")) return true;
  return text.includes("not visible") || text.includes("no visible");
}

function normalizeWearItem(text: string): string {
  const t = stripYesPrefix(text);
  if (!t) return "";
  const comma = t.indexOf(",");
  if (comma > 0) {
    const left = t.slice(0, comma).trim();
    const right = t.slice(comma + 1).trim();
    if (left && right && !isNegative(left) && !isNegative(right)) {
      if (AFFIRM.has(left.toLowerCase())) return right.toLowerCase();
      return `${right.toLowerCase()} ${left.toLowerCase()}`;
    }
  }
  return t.toLowerCase();
}

function formatAccessoryPhrase(text: string): string {
  const acc = stripYesPrefix(text).toLowerCase().trim();
  if (!acc) return "";
  if (acc.startsWith("wearing ")) return acc;
  if (acc.startsWith("a ") || acc.startsWith("an ")) return `wearing ${acc}`;
  return `wearing a ${acc}`;
}

/** Natural-language caption for lists (matches embedder prose). */
export function formatEmbedCaption(answers: string[]): string {
  const padded = [...answers];
  while (padded.length < 10) padded.push("Unknown");
  const [sex, age, skin, hair, headwear, upper, lower, footwear, accessories] = padded;

  const segments: string[] = [];

  const demo: string[] = [];
  if (!isNegative(sex)) demo.push(sex.toLowerCase());
  if (!isNegative(age)) demo.push(age.toLowerCase());
  if (!isNegative(skin)) {
    const sk = skin.toLowerCase();
    demo.push(sk.includes("skin") ? sk : `${sk} colored skin`);
  }
  if (demo.length) segments.push(demo.join(" "));

  if (!isNegative(hair)) {
    const h = hair.toLowerCase();
    segments.push(h.includes("hair") ? h : `${h} hair`);
  }

  const hwRaw = !isNegative(headwear) ? stripYesPrefix(headwear) : "";
  const hw = hwRaw ? normalizeWearItem(hwRaw) : null;
  const wear = [upper, lower, footwear]
    .filter((item) => !isNegative(item))
    .map(normalizeWearItem)
    .filter(Boolean);

  const hasAccessory = !isNegative(accessories) && Boolean(stripYesPrefix(accessories));

  if (hw || wear.length) {
    const items = [...(hw ? [hw] : []), ...wear];
    let clause: string;
    if (items.length === 1) {
      clause = `wearing ${items[0]}`;
    } else if (hasAccessory) {
      clause = `wearing ${items.join(", ")}`;
    } else {
      clause = `wearing ${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
    }
    segments.push(clause);
  }

  if (hasAccessory) {
    const acc = formatAccessoryPhrase(accessories);
    if (acc) segments.push(`and ${acc}`);
  }

  const out = segments.join(", ");
  if (!out) return "";
  const prose = out.endsWith(".") ? out : `${out}.`;
  return prose.length > 1 ? prose[0].toUpperCase() + prose.slice(1) : prose.toUpperCase();
}

/** Prose under thumbnails — stored caption_prose, or computed from numbered summary. */
export function trackCaptionProse(
  track: { summary: string; caption_prose?: string },
  prompt?: string,
): string {
  const stored = track.caption_prose?.trim();
  if (stored) return stored;
  return formatPublicCaption(track.summary, prompt);
}

/** Tile / details lead: appearance prose, or the activity line when there is no Q1–Q9. */
export function trackCardMainText(
  track: {
    summary: string;
    caption_prose?: string;
    activity?: string;
    activities_distinct?: string[];
  },
  prompt?: string,
): string {
  const prose = trackCaptionProse(track, prompt);
  if (prose) return prose;
  return trackActivityLine(track, prompt);
}

export function extractPromptLabels(prompt: string): string[] {
  const labels: string[] = [];
  for (const line of prompt.split("\n")) {
    const m = line.match(QUESTION_LINE);
    if (!m) continue;
    const question = m[1];
    const rule = LABEL_RULES.find((r) => r.pattern.test(question));
    if (rule) {
      labels.push(rule.label);
      continue;
    }
    const word = question.split(/\s+/)[0]?.replace(/\?$/, "") ?? "Answer";
    labels.push(word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }
  if (labels.length >= 10 && !labels.some((l) => l.toLowerCase() === "activity")) {
    labels[labels.length - 1] = "Activity";
  }
  return labels;
}

export function countPromptQuestions(prompt: string): number {
  let n = 0;
  for (const line of prompt.split("\n")) {
    if (QUESTION_LINE.test(line)) n++;
  }
  return n;
}

function parseNumberedAnswers(summary: string, count: number): string[] | null {
  const byNum = new Map<number, string>();
  for (const line of summary.split("\n")) {
    const m = line.match(NUMBERED_ANSWER);
    if (!m) continue;
    const n = Number(m[1]);
    if (n >= 1 && n <= count) byNum.set(n, m[2].trim());
  }
  if (byNum.size === 0) return null;
  return Array.from({ length: count }, (_, i) => byNum.get(i + 1) ?? "—");
}

/** Split stored caption into one answer per prompt question. */
export function parseCaptionAnswers(summary: string, count: number): string[] {
  const text = summary.trim();
  if (!text || count <= 0) return [];

  const numbered = parseNumberedAnswers(text, count);
  if (numbered) return numbered;

  const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length === count) return parts;

  if (parts.length === 1) {
    const out = Array<string>(count).fill("—");
    out[0] = parts[0];
    return out;
  }

  if (parts.length < count) {
    return [...parts, ...Array(count - parts.length).fill("—")];
  }

  if (count >= 10) {
    const gender = parts[0];
    const age = parts[1];
    const skin = parts[2];
    const hair = parts[3];
    const activity = parts[parts.length - 1];
    const accessories = parts[parts.length - 2];
    const footwear = parts[parts.length - 3];
    const middle = parts.slice(4, parts.length - 3);

    let headwear = "—";
    let upper = "—";
    let lower = "—";

    if (middle.length === 1) headwear = middle[0];
    else if (middle.length === 2) {
      headwear = middle[0];
      upper = middle[1];
    } else if (middle.length === 3) {
      [headwear, upper, lower] = middle;
    } else if (middle.length > 3) {
      headwear = middle[0];
      const rest = middle.slice(1);
      const half = Math.max(1, Math.ceil(rest.length / 2));
      upper = rest.slice(0, half).join(" ");
      lower = rest.slice(half).join(" ");
    }

    return [gender, age, skin, hair, headwear, upper, lower, footwear, accessories, activity].slice(
      0,
      count,
    );
  }

  return [...parts.slice(0, count - 1), parts.slice(count - 1).join(", ")];
}

/** Q10 on the combined vLLM request — same string captioner sends when both sides are on. */
export const COMBINED_ACTIVITY_LINE =
  "10. Describe in details what this person is doing.";

/** Appearance + activity prompts from config, concatenated the way captioner composes them. */
export function captionPromptFromConfig(vllm: unknown): string {
  if (!vllm || typeof vllm !== "object") return "";
  const v = vllm as Record<string, unknown>;
  const appearance =
    typeof v.appearance_prompt === "string" ? v.appearance_prompt.trim() : "";
  const activity =
    typeof v.activity_prompt === "string" ? v.activity_prompt.trim() : "";
  if (appearance && activity) return `${appearance}\n${COMBINED_ACTIVITY_LINE}`;
  if (appearance) return appearance;
  if (activity) return activity;
  return typeof v.prompt === "string" ? v.prompt : "";
}

export function buildDescriptionRows(prompt: string, summary: string): DescriptionRow[] {
  const labels = extractPromptLabels(prompt);
  if (labels.length === 0) {
    return summary.trim() ? [{ label: "Caption", answer: summary.trim() }] : [];
  }
  const fieldCount = countPromptQuestions(prompt) || labels.length;
  const answers = parseCaptionAnswers(summary, fieldCount);
  return labels.map((label, i) => ({
    label,
    answer: answers[i]?.trim() || "—",
  }));
}

/** Caption text for UI lists — appearance prose (Q1–Q9). Activity is shown separately. */
export function formatPublicCaption(summary: string, prompt?: string): string {
  const text = summary.trim();
  if (!text) return "";
  const fieldCount = prompt ? countPromptQuestions(prompt) : 10;
  const answers = parseCaptionAnswers(text, fieldCount);
  if (parseNumberedAnswers(text, fieldCount)) {
    return formatEmbedCaption(answers.slice(0, 9));
  }
  if (fieldCount >= 10 && answers.length >= 10) {
    return formatEmbedCaption(answers.slice(0, 9));
  }
  return text;
}

export function captionAnswerForLabel(
  prompt: string,
  summary: string,
  label: string,
): string {
  const needle = label.toLowerCase();
  for (const row of buildDescriptionRows(prompt, summary)) {
    if (row.label.toLowerCase() === needle) {
      return row.answer.trim();
    }
  }
  return "";
}

export function matchesCaptionFilters(
  prompt: string,
  summary: string,
  filters: { sex?: string; age?: string; skin?: string; hair?: string },
): boolean {
  const norm = (v: string) => v.trim().toLowerCase();
  const matchField = (label: string, want: string) => {
    const ans = norm(captionAnswerForLabel(prompt, summary, label));
    if (!ans || ans === "—") return false;
    return ans === norm(want) || ans.includes(norm(want));
  };
  if (filters.sex && !matchField("Sex", filters.sex)) return false;
  if (filters.age && !matchField("Age", filters.age)) return false;
  if (filters.skin && !matchField("Skin", filters.skin)) return false;
  if (filters.hair && !matchField("Hair", filters.hair)) return false;
  return true;
}

function isBlankActivity(value: string): boolean {
  const text = value.trim().toLowerCase().replace(/\.$/, "");
  return !text || NEG.has(text) || text === "—";
}

/** Activity line for tiles and details: stored field, then Q10, then chips. */
export function trackActivityLine(
  track: { activity?: string; summary: string; activities_distinct?: string[] },
  prompt?: string,
): string {
  const stored = track.activity?.trim();
  if (stored && !isBlankActivity(stored)) return stored;

  if (prompt) {
    const labeled = captionAnswerForLabel(prompt, track.summary, "Activity");
    if (labeled && labeled !== "—" && !isBlankActivity(labeled)) return labeled;
  }

  const fieldCount = prompt ? countPromptQuestions(prompt) : 10;
  if (fieldCount >= 10) {
    const q10 = parseCaptionAnswers(track.summary, fieldCount)[9]?.trim() ?? "";
    if (q10 && q10 !== "—" && !isBlankActivity(q10)) return q10;
  }

  for (const a of track.activities_distinct ?? []) {
    if (a.trim() && !isBlankActivity(a)) return a.trim();
  }
  return "";
}
